import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type ProjectSummary,
  type ScanSummary,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { FormField } from '../components/FormField';
import { InlineAlert } from '../components/InlineAlert';
import { HelpTip } from '../components/HelpTip';
import { useOrg } from '../org/context';
import { useAuth } from '../auth/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError, toFieldErrors, type DisplayError } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import { ScanRow, type StopOutcome } from './ScanRow';
import styles from './ScansList.module.css';

type LoadState = 'loading' | 'error' | 'ready';

export interface ScansListProps {
  client?: ApiClient;
}

function isConflict(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    (err as { code: unknown }).code === 'conflict'
  );
}

/** Now, as a `datetime-local` value (local wall-clock, minute precision) — the
 * input's `min` so the native picker nudges away from past times. */
function localNowValue(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

/**
 * Project scans (#102, slice 2). Loads the project (for the verification gate)
 * and its scans together, offers a verification-gated Run-scan control and a
 * manual Refresh, and lists every scan most-recent-first. Async results announce
 * in a polite live region (mirrors Members); action errors take focus in an
 * assertive alert (mirrors DomainVerification).
 */
export function ScansList({ client = defaultClient }: ScansListProps) {
  const { currentOrg } = useOrg();
  const { userId } = useAuth();
  const { id } = useParams<{ id: string }>();
  useFocusMain();

  const orgId = currentOrg?.id;

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [project, setProject] = useState<ProjectSummary | null>(null);
  const [scans, setScans] = useState<ScanSummary[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Optional one-off schedule (#332): a `datetime-local` string; empty = run now.
  const [scheduledLocal, setScheduledLocal] = useState('');
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  const errorRef = useRef<HTMLDivElement>(null);

  // Status-cell focus anchors, keyed by scan id, so focus survives the Stop
  // control unmounting after a stop/stale outcome (mirrors Members' removeRefs).
  const statusRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());
  // null = nothing pending; a scan id = focus that row's Status anchor after the
  // next scans re-render (deferred, like Members' pendingFocus).
  const pendingStatusFocus = useRef<string | null>(null);

  useEffect(() => {
    if (actionError) errorRef.current?.focus();
  }, [actionError]);

  useEffect(() => {
    if (pendingStatusFocus.current === null) return;
    const target = pendingStatusFocus.current;
    pendingStatusFocus.current = null;
    statusRefs.current.get(target)?.focus();
  }, [scans]);

  const registerStatusRef = useCallback((scanId: string, el: HTMLTableCellElement | null) => {
    if (el) statusRefs.current.set(scanId, el);
    else statusRefs.current.delete(scanId);
  }, []);

  const load = useCallback(async () => {
    if (!orgId || !id) return;
    setPhase('loading');
    try {
      const [nextProject, nextScans] = await Promise.all([
        client.getProject(orgId, id),
        client.listScans(orgId, id),
      ]);
      setProject(nextProject);
      setScans(nextScans);
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    if (!orgId || !id || refreshing) return;
    setRefreshing(true);
    setActionError(null);
    try {
      const [nextProject, nextScans] = await Promise.all([
        client.getProject(orgId, id),
        client.listScans(orgId, id),
      ]);
      setProject(nextProject);
      setScans(nextScans);
      setStatusMessage('Scans updated.');
    } catch (err) {
      setActionError(toDisplayError(err).message);
    } finally {
      setRefreshing(false);
    }
  }, [client, orgId, id, refreshing]);

  const run = useCallback(async () => {
    if (!orgId || !id || running) return;
    const trimmed = scheduledLocal.trim();
    let body: { scheduledAtMs?: number } | undefined;
    if (trimmed) {
      const ms = new Date(trimmed).getTime();
      if (Number.isNaN(ms)) {
        setScheduleError('Enter a valid date and time.');
        return;
      }
      body = { scheduledAtMs: ms };
    }
    setRunning(true);
    setActionError(null);
    setScheduleError(null);
    try {
      await client.runScan(orgId, id, body);
      const [nextProject, nextScans] = await Promise.all([
        client.getProject(orgId, id),
        client.listScans(orgId, id),
      ]);
      setProject(nextProject);
      setScans(nextScans);
      setScheduledLocal('');
      setStatusMessage(body ? 'Scan scheduled.' : 'Scan started.');
    } catch (err) {
      // A past/too-far schedule comes back as a 422 field error — surface it on the
      // picker, not the top-level alert.
      const fieldErr = toFieldErrors(err).scheduledAtMs;
      if (fieldErr) {
        setScheduleError(fieldErr);
      } else {
        setActionError(toDisplayError(err).message);
        // A 409 means the gate moved under us (e.g. the domain is no longer
        // verified) — refetch so the gate and list self-heal to the truth.
        if (isConflict(err)) {
          try {
            const [nextProject, nextScans] = await Promise.all([
              client.getProject(orgId, id),
              client.listScans(orgId, id),
            ]);
            setProject(nextProject);
            setScans(nextScans);
          } catch {
            // Best-effort self-heal; the original action error stays surfaced.
          }
        }
      }
    } finally {
      setRunning(false);
    }
  }, [client, orgId, id, running, scheduledLocal]);

  // Refetch after a stop attempt. When `focusScanId` is set, defer focus to that
  // row's Status anchor once the fresh list renders; pass null when an assertive
  // alert (a 403) should keep focus instead.
  const refetchScans = useCallback(
    async (focusScanId: string | null) => {
      if (!orgId || !id) return;
      try {
        const [nextProject, nextScans] = await Promise.all([
          client.getProject(orgId, id),
          client.listScans(orgId, id),
        ]);
        setProject(nextProject);
        if (focusScanId) pendingStatusFocus.current = focusScanId;
        setScans(nextScans);
      } catch {
        // Best-effort self-heal; a surfaced action error (if any) stays visible.
      }
    },
    [client, orgId, id],
  );

  const handleStopped = useCallback(
    (scanId: string, outcome: StopOutcome) => {
      if (outcome === 'stopped') setStatusMessage('Scan stopped.');
      void refetchScans(scanId);
    },
    [refetchScans],
  );

  const handleStopForbidden = useCallback(
    (display: DisplayError) => {
      setActionError(display.message);
      void refetchScans(null);
    },
    [refetchScans],
  );

  const isVerified = project?.domainVerificationStatus === 'verified';

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 id="scans-heading" className={styles.heading}>
          Scans
        </h1>
        <p className={styles.subheading}>
          Production scans of this project, most recent first.{' '}
          <HelpTip term="scan" />
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading scans…</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to="/" className={styles.backLink}>
              Back to home
            </Link>
          </div>
        ) : null}

        {phase === 'ready' ? (
          <>
            <InlineAlert ref={errorRef} severity="error">
              {actionError}
            </InlineAlert>
            <InlineAlert severity="success">{statusMessage}</InlineAlert>

            {!isVerified ? (
              <InlineAlert severity="info">
                Verify your store domain before you can run a scan.{' '}
                <Link to={`/projects/${id ?? ''}/domain-verification`}>
                  Verify your store domain
                </Link>
              </InlineAlert>
            ) : null}

            {isVerified ? (
              <div className={styles.scheduleField}>
                <FormField
                  label="Run later (optional)"
                  helpText="Schedule this scan for a future time, up to 90 days ahead. Leave blank to run now."
                  {...(scheduleError ? { error: scheduleError } : {})}
                >
                  {({ controlProps }) => (
                    <input
                      {...controlProps}
                      type="datetime-local"
                      className={styles.datetime}
                      min={localNowValue()}
                      value={scheduledLocal}
                      onChange={(e) => setScheduledLocal(e.target.value)}
                    />
                  )}
                </FormField>
              </div>
            ) : null}

            <div className={styles.actions}>
              {isVerified ? (
                <Button busy={running} onClick={run}>
                  {running
                    ? scheduledLocal
                      ? 'Scheduling…'
                      : 'Starting…'
                    : scheduledLocal
                      ? 'Schedule scan'
                      : 'Run scan'}
                </Button>
              ) : null}
              <Button variant="secondary" busy={refreshing} onClick={refresh}>
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
              <Link to={`/projects/${id ?? ''}/journey-health`} className={styles.backLink}>
                View journey health
              </Link>
            </div>

            {!isVerified ? (
              <div className={styles.gate}>
                <InlineAlert severity="info">
                  Verify this domain before running a scan.{' '}
                  <Link to={`/projects/${id ?? ''}/domain-verification`}>Verify domain</Link>
                </InlineAlert>
              </div>
            ) : null}

            {scans.length === 0 ? (
              <p className={styles.empty}>No scans yet. Run a scan to see results here.</p>
            ) : (
              <table className={styles.table} aria-labelledby="scans-heading">
                <thead>
                  <tr>
                    <th scope="col">Status</th>
                    <th scope="col">Started</th>
                    <th scope="col">Initiated by</th>
                    <th scope="col">Completed</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scans.map((scan) => (
                    <ScanRow
                      key={scan.id}
                      scan={scan}
                      {...(id ? { projectId: id } : {})}
                      userId={userId}
                      role={currentOrg?.role ?? null}
                      onStopped={handleStopped}
                      onForbidden={handleStopForbidden}
                      stopScan={(scanId) => client.stopScan(orgId ?? '', id ?? '', scanId)}
                      registerStatusRef={registerStatusRef}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </>
        ) : null}
      </Card>
    </AppShell>
  );
}
